import { useEffect, useMemo, useRef } from 'react'
import type { LayoutProps } from 'src/Layout'
import { getBlogPathSegments } from 'src/utils/analytics/blog/getBlogArticleSection'
import { getBlogPageNumber } from 'src/utils/analytics/blog/getBlogPageNumber'

type PageTypeProps = {
  serverData: LayoutProps['serverData']
  location: LayoutProps['location']
  '*'?: string
  slug?: string
}

type PageInfo = { type: string; name: string }

export type PageTypeResult = {
  currentPageType: string
  previousPageType: string
  currentPageName: string
  previousPageName: string
}

export const PAGE_TYPE_MAP = {
  Home: 'Página de início',
  collection: 'Página de lista de produtos',
  category: 'Página de lista de produtos',
  pdp: 'Página de detalhes do produto',
  Checkout: 'Página de finalização',
  Account: 'Página de minha conta',
  Campanha: 'Página de campanha',
  Outro: 'Outro',
  Pesquisa: 'Página de busca',
  Esportes: 'Página de esportes',
  'Cartao presente': 'Página de cartão presente',
  Marcas: 'Página de marcas',
}

const TAXONOMY_MAP: Record<string, string> = {
  shipping: 'Entrega',
  cart: 'Carrinho',
  profile: 'Meus dados',
}

const STATIC_ROUTES: Record<string, string> = {
  '/account/orders': 'Página de meus pedidos',
  '/account/wishlist': 'Página de lista de desejos',
  '/checkout/cart': 'Página de carrinho',
}

const formatSlug = (path?: string) => {
  const parts = path?.split('/')

  return parts?.[parts.length - 1]?.replaceAll('-', ' ') ?? ''
}

const getBlogPageInfo = (pathname: string, search = ''): PageInfo | null => {
  const segments = getBlogPathSegments(pathname)

  if (segments[0] !== 'blog') {
    return null
  }

  if (segments.length === 1) {
    return { type: 'Blog Home', name: 'Blog Home' }
  }

  if (segments[1] === 'autor') {
    const authorSlug = segments[2] ?? ''

    return {
      type: 'Blog Author',
      name: formatSlug(authorSlug) || 'Blog Author',
    }
  }

  if (segments[1] === '404') {
    return { type: 'Blog', name: 'Blog 404' }
  }

  if (segments.length === 2) {
    const categoryName = formatSlug(segments[1]) || 'Blog Category'
    const currentPage = getBlogPageNumber(search)

    return {
      type: 'Blog Category',
      name:
        currentPage > 1
          ? `${categoryName} - Página ${currentPage}`
          : categoryName,
    }
  }

  if (segments.length >= 3) {
    const articleSlug = segments[segments.length - 1]
    const pageTitle =
      typeof document !== 'undefined' ? document.title.split('|')[0].trim() : ''

    return {
      type: 'Blog Article',
      name: pageTitle || formatSlug(articleSlug) || 'Blog Article',
    }
  }

  return { type: 'Blog', name: 'Blog' }
}

const getPageName = (
  pageType: string,
  path: string,
  taxonomy?: string
): PageInfo => {
  const staticName = STATIC_ROUTES[path]

  if (staticName) return { type: staticName, name: staticName }

  const typeMapped =
    PAGE_TYPE_MAP[pageType as keyof typeof PAGE_TYPE_MAP] ?? pageType

  const taxMapped = TAXONOMY_MAP[taxonomy ?? ''] ?? taxonomy ?? ''

  return {
    type: typeMapped,
    name: taxMapped ? `${typeMapped}: ${taxMapped}` : typeMapped,
  }
}

export const usePageType = ({
  serverData,
  location,
  slug,
  ...props
}: PageTypeProps) => {
  const wildcardRoute = props['*']
  const currentPageType = serverData?.pageType ?? 'Outro'

  const currentTaxonomy = useMemo(() => {
    if (currentPageType === 'collection') return formatSlug(slug)

    return (
      serverData?.searchParams?.term?.replaceAll('-', ' ') ??
      wildcardRoute ??
      formatSlug(slug)
    )
  }, [currentPageType, serverData?.searchParams?.term, slug, wildcardRoute])

  const previousPageRef = useRef({
    type: currentPageType,
    taxonomy: currentTaxonomy,
    path: location.pathname,
    search: location.search,
  })

  const current = useMemo(() => {
    const blogPage = getBlogPageInfo(location.pathname, location.search)

    if (blogPage) {
      return blogPage
    }

    return getPageName(currentPageType, location.pathname, currentTaxonomy)
  }, [currentPageType, currentTaxonomy, location.pathname, location.search])

  const previous = (() => {
    const blogPage = getBlogPageInfo(
      previousPageRef.current.path,
      previousPageRef.current.search
    )

    if (blogPage) {
      return blogPage
    }

    return getPageName(
      previousPageRef.current.type,
      previousPageRef.current.path,
      previousPageRef.current.taxonomy
    )
  })()

  useEffect(() => {
    if (
      previousPageRef.current.path === location.pathname &&
      previousPageRef.current.search === location.search
    ) {
      return
    }

    previousPageRef.current = {
      type: currentPageType,
      taxonomy: currentTaxonomy || '',
      path: location.pathname,
      search: location.search,
    }
  }, [currentPageType, currentTaxonomy, location.pathname, location.search])

  return useMemo(
    () => ({
      currentPageType: current.type,
      previousPageType: previous.type,
      currentPageName: current.name,
      previousPageName: previous.name,
    }),
    [current.name, current.type, previous.name, previous.type]
  )
}
